import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const DigitalDashboardDetailsPage = () => {
  const specifications = [
    { label: 'Screen Size', value: '12.3" Full LCD Display' },
    { label: 'Resolution', value: '1920 x 720 HD' },
    { label: 'Compatibility', value: 'BMW 3/4/5/6/7 Series & X Models' },
    { label: 'Installation', value: 'Plug & Play - No Cable Cutting' },
    { label: 'Processor', value: 'Qualcomm Snapdragon' },
    { label: 'Memory', value: '4GB RAM + 64GB Storage' },
    { label: 'Operating System', value: 'Android 13' },
    { label: 'Warranty', value: '2 Years Manufacturer Warranty' },
  ];

  const features = [
    {
      icon: 'ri-dashboard-3-line',
      title: 'Customizable Display Modes',
      description: 'Multiple theme options including Sport, Comfort, and Eco modes with real-time switching',
    },
    {
      icon: 'ri-navigation-line',
      title: 'Built-in GPS Navigation',
      description: 'Integrated navigation system with real-time traffic updates and voice guidance',
    },
    {
      icon: 'ri-music-line',
      title: 'Media Integration',
      description: 'Seamless connection with Apple CarPlay, Android Auto, and Bluetooth audio',
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Vehicle Diagnostics',
      description: 'Real-time monitoring of engine performance, fuel consumption, and maintenance alerts',
    },
    {
      icon: 'ri-sun-line',
      title: 'Auto Brightness',
      description: 'Automatic brightness adjustment based on ambient light conditions',
    },
    {
      icon: 'ri-plug-line',
      title: 'OBD Integration',
      description: 'Direct connection to vehicle OBD system for accurate data display',
    },
  ];

  const galleryImages = [
    'https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/ee78eaa9f99bdeb4e9779fd1f14ea700.png',
    'https://public.readdy.ai/ai/img_res/2b8386cc245e10e8abfe058478d56f4b.jpg',
    'https://readdy.ai/api/search-image?query=BMW%20digital%20instrument%20panel%20LCD%20display%20showing%20navigation%20map%20GPS%20route%20modern%20car%20dashboard%20blue%20ambient%20lighting%20luxury%20vehicle%20interior%20professional%20photography%20detailed%20view&width=800&height=600&seq=bmwdash002&orientation=landscape',
    'https://readdy.ai/api/search-image?query=BMW%20digital%20cluster%20installation%20process%20plug%20and%20play%20connection%20factory%20pins%20wiring%20harness%20automotive%20technician%20hands%20professional%20installation%20workshop%20environment&width=800&height=600&seq=bmwdash003&orientation=landscape',
  ];

  return (
    <div className="min-h-screen bg-primary-black">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950"></div>
          <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-amber-600/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Product Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden border-2 border-amber-500/20 shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/ee78eaa9f99bdeb4e9779fd1f14ea700.png"
                  alt="BMW Digital Dashboard"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-amber-500 text-black px-6 py-3 rounded-xl font-bold text-lg shadow-xl">
                <i className="ri-verified-badge-line mr-2"></i>
                OEM Quality
              </div>
            </div>

            {/* Right: Product Info */}
            <div>
              <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full">
                <i className="ri-star-fill text-amber-500"></i>
                <span className="text-amber-500 text-sm font-semibold">Premium Upgrade</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                BMW Digital Dashboard Cluster
              </h1>
              <p className="text-xl text-zinc-400 mb-6">
                Transform your BMW with a state-of-the-art 12.3" full LCD digital instrument cluster. 
                Experience modern technology with plug-and-play installation.
              </p>

              <div className="flex items-center gap-4 mb-8">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-2xl text-green-500"></i>
                  <span className="text-zinc-300">2 Year Warranty</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-tools-line text-2xl text-amber-500"></i>
                  <span className="text-zinc-300">Expert Installation</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href="#contact"
                  className="px-8 py-4 bg-amber-500 text-black font-semibold rounded-full hover:bg-amber-400 transition-colors cursor-pointer whitespace-nowrap text-center"
                >
                  <i className="ri-calendar-line mr-2"></i>
                  Book Installation
                </a>
                <a
                  href="https://www.aliexpress.us/item/3256807019053831.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 border-2 border-amber-500 text-amber-500 font-semibold rounded-full hover:bg-amber-500 hover:text-black transition-colors cursor-pointer whitespace-nowrap text-center"
                >
                  <i className="ri-external-link-line mr-2"></i>
                  View Product Details
                </a>
              </div>

              <p className="text-sm text-zinc-500">
                <i className="ri-information-line mr-1"></i>
                Professional installation recommended. Contact us for a free consultation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Specifications Section */}
      <section className="py-16 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Technical Specifications
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {specifications.map((spec, index) => (
              <div
                key={index}
                className="bg-zinc-950 border border-zinc-800 rounded-xl p-6 hover:border-amber-500/30 transition-colors"
              >
                <div className="text-zinc-500 text-sm mb-2">{spec.label}</div>
                <div className="text-white font-semibold">{spec.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Advanced Features
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Packed with cutting-edge technology to enhance your driving experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-amber-500/30 transition-all hover:-translate-y-1"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-amber-500/10 rounded-xl mb-4">
                  <i className={`${feature.icon} text-3xl text-amber-500`}></i>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-zinc-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Product Gallery
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                className="relative rounded-xl overflow-hidden border border-zinc-800 hover:border-amber-500/30 transition-colors group"
              >
                <img
                  src={image}
                  alt={`BMW Digital Dashboard ${index + 1}`}
                  className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Plug & Play Installation
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              No cutting of original cables required. Everything connects directly to your factory pins.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Remove Old Cluster', icon: 'ri-tools-line' },
              { step: '02', title: 'Connect to Factory Pins', icon: 'ri-plug-line' },
              { step: '03', title: 'Install New Display', icon: 'ri-install-line' },
              { step: '04', title: 'Configure & Test', icon: 'ri-check-double-line' },
            ].map((item, index) => (
              <div
                key={index}
                className="relative bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center"
              >
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 bg-amber-500 text-black rounded-full flex items-center justify-center font-bold text-sm">
                  {item.step}
                </div>
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-amber-500/10 rounded-full mt-4">
                  <i className={`${item.icon} text-3xl text-amber-500`}></i>
                </div>
                <h3 className="text-white font-semibold">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-zinc-950 to-zinc-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Upgrade Your BMW?
          </h2>
          <p className="text-xl text-zinc-400 mb-10">
            Contact us today for professional installation and expert advice
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <a
              href="#contact"
              className="px-10 py-5 bg-amber-500 text-black font-semibold text-lg rounded-full hover:bg-amber-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-2"></i>
              Schedule Installation
            </a>
            <a
              href="tel:+1234567890"
              className="px-10 py-5 border-2 border-zinc-700 text-white font-semibold text-lg rounded-full hover:border-amber-500 hover:text-amber-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call for Quote
            </a>
          </div>
          <div className="mt-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-amber-500 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DigitalDashboardDetailsPage;
